import type { RefObject } from 'react';
import { useCallback, useEffect, useLayoutEffect } from 'react';
import { useAppDispatch } from '@/app/store/store';
import {
  setDesktopRecommendedBrowseFilterStripStuck,
  setMobileRecommendedFilterStripStuck,
} from '@/app/store/newHomeUiSlice';
import {
  isRecommendedCategoryFilterStripStuck,
  isRecommendedCategoryFilterStripShouldHideDesktopNav,
} from '@/features/newHome/utils/recommendedCategoryStripSentinel';
import { useIsDesktop, useIsMobile } from '@/core/hooks/useMediaQuery';

/**
 * Keeps `newHomeUi` strip-stuck flags in sync for FloatingNavigation (mobile) and DesktopNavbar
 * fallback (desktop), using the sentinel node above the sticky category row.
 */
export function useRecommendedFilterStripStuckSync(
  stickySentinelRef: RefObject<HTMLDivElement | null>,
  tabsLength: number,
  listingDataKey: string,
  isLoading: boolean,
  productsLength: number,
): void {
  const dispatch = useAppDispatch();
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();

  const clearStuck = useCallback(() => {
    dispatch(setMobileRecommendedFilterStripStuck(false));
    dispatch(setDesktopRecommendedBrowseFilterStripStuck(false));
  }, [dispatch]);

  const applyFromSentinel = useCallback(() => {
    const el = stickySentinelRef.current;
    if (tabsLength === 0 || !el) return;
    const stuck = isMobile
      ? isRecommendedCategoryFilterStripStuck(el)
      : isDesktop
        ? isRecommendedCategoryFilterStripShouldHideDesktopNav(el)
        : false;
    if (isMobile) {
      dispatch(setMobileRecommendedFilterStripStuck(stuck));
    } else if (isDesktop) {
      dispatch(setDesktopRecommendedBrowseFilterStripStuck(stuck));
    } else {
      clearStuck();
    }
  }, [clearStuck, dispatch, isDesktop, isMobile, tabsLength, stickySentinelRef]);

  useLayoutEffect(() => {
    if (tabsLength === 0) {
      clearStuck();
      return;
    }
    applyFromSentinel();
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(applyFromSentinel);
    });
    return () => window.cancelAnimationFrame(id);
  }, [
    applyFromSentinel,
    clearStuck,
    isLoading,
    listingDataKey,
    productsLength,
    tabsLength,
  ]);

  useEffect(() => {
    if (tabsLength === 0) {
      clearStuck();
      return;
    }
    const el = stickySentinelRef.current;
    if (!el) return;
    let rafId: number | null = null;
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        applyFromSentinel();
      });
    };
    const obs = new IntersectionObserver(applyFromSentinel, { threshold: 0 });
    obs.observe(el);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      obs.disconnect();
      window.removeEventListener('scroll', onScroll);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      clearStuck();
    };
  }, [
    applyFromSentinel,
    clearStuck,
    isDesktop,
    isMobile,
    listingDataKey,
    stickySentinelRef,
    tabsLength,
  ]);

  useEffect(() => {
    if (tabsLength === 0) return;
    const sync = () => applyFromSentinel();
    if (document.readyState === 'complete') {
      window.requestAnimationFrame(() => window.requestAnimationFrame(sync));
    } else {
      window.addEventListener('load', sync, { once: true });
    }
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) window.requestAnimationFrame(sync);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [applyFromSentinel, listingDataKey, tabsLength]);
}
